import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { TrendUp, TrendDown, ChartLine, Calculator, CreditCard, Bell, Sparkle, Bank, Handshake, Buildings, Warning, CurrencyDollar } from '@phosphor-icons/react';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/summary').then((r) => setData(r.data)),
      api.get('/reminders').then((r) => setReminders(r.data)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-[#1E3F2D] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const d = data || {};

  return (
    <div className="space-y-5 animate-fade-in" data-testid="dashboard">
      {reminders.length > 0 && (
        <div className="bg-gradient-to-r from-[#A63C24] via-[#D98324] to-[#1E3F2D] rounded-xl overflow-hidden" data-testid="notification-ticker">
          <div className="relative overflow-hidden py-2.5 px-4">
            <div className="flex animate-ticker whitespace-nowrap">
              {[...reminders, ...reminders].map((r, i) => (
                <span key={i} className="inline-flex items-center gap-2 mx-8 text-white text-sm font-medium">
                  {r.urgency === 'high' ? <Warning size={14} weight="fill" className="text-yellow-200 flex-shrink-0" /> : <Bell size={14} weight="fill" className="text-white/70 flex-shrink-0" />}
                  <span>{r.title}</span>
                  {r.amount > 0 && <span className="font-mono-numbers opacity-80">({fmt(r.amount)})</span>}
                  <span className="text-white/30 mx-4">|</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Welcome back, {user?.name?.split(' ')[0] || 'there'}</h1>
        <p className="text-[#5C5A56] text-sm mt-1">Your financial overview for this month</p>
      </div>

      <div className="bg-gradient-to-br from-[#1E3F2D] to-[#265C42] rounded-2xl shadow-sm p-5 text-white" data-testid="net-worth-banner">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-60">Net Worth</p>
            <p className="text-3xl font-bold font-mono-numbers mt-1" data-testid="net-worth-value">{fmt(d.net_worth)}</p>
            <p className="text-xs opacity-50 mt-1">Assets ({fmt(d.total_assets)}) - Liabilities ({fmt(d.total_liabilities)})</p>
          </div>
          <CurrencyDollar size={40} weight="duotone" className="opacity-20" />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="summary-cards">
        <MetricCard icon={Bank} label="Bank Balance" value={fmt(d.total_bank_balance)} color="#1E3F2D" />
        <MetricCard icon={TrendUp} label="Income" value={fmt(d.total_income)} color="#265C42" />
        <MetricCard icon={TrendDown} label="Expenses" value={fmt(d.total_expenses)} color="#A63C24" />
        <MetricCard icon={ChartLine} label="Invested" value={fmt(d.total_invested)} color="#D98324" />
        <MetricCard icon={Buildings} label="Properties" value={fmt(d.total_property)} color="#1E3F2D" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniCard icon={Calculator} label="Monthly EMI" value={fmt(d.total_emi)} />
        <MiniCard icon={Handshake} label="Lent Out" value={fmt(d.total_lent)} />
        <MiniCard icon={Handshake} label="Borrowed" value={fmt(d.total_borrowed)} />
        <MiniCard icon={CreditCard} label="Pending Bills" value={fmt(d.pending_payments_amount)} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <MiniCard icon={Calculator} label="EMI Outstanding" value={fmt(d.total_emi_outstanding)} />
        <MiniCard icon={Bell} label="Alerts" value={d.unread_alerts || 0} highlight={d.unread_alerts > 0} />
        <MiniCard icon={CreditCard} label="Bills Count" value={d.upcoming_payments_count || 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5" data-testid="recent-transactions">
          <h3 className="text-base font-semibold text-[#1C1B1A] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Transactions</h3>
          {(d.recent_transactions || []).length > 0 ? (
            <div className="space-y-2">
              {d.recent_transactions.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-[#E8E6E1] last:border-0">
                  <div><p className="text-sm font-medium text-[#1C1B1A]">{t.description || t.category}</p><p className="text-xs text-[#5C5A56]">{t.category} &middot; {t.date?.slice(0, 10)}</p></div>
                  <span className={`font-mono-numbers text-sm font-semibold ${t.type === 'income' ? 'text-[#265C42]' : 'text-[#A63C24]'}`}>{t.type === 'income' ? '+' : '-'}{fmt(t.amount)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[#5C5A56] text-sm text-center py-6">No transactions yet</p>}
        </div>
        <div className="bg-gradient-to-br from-[#FDF7F0] to-[#F5EBE1] border border-[#E8DCCB] rounded-2xl shadow-sm p-5 relative overflow-hidden" data-testid="ai-insight-card">
          <div className="flex items-center gap-2 mb-3"><Sparkle size={18} weight="fill" className="text-[#D98324]" /><h3 className="text-base font-semibold text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Insight</h3></div>
          <p className="text-sm text-[#5C5A56] leading-relaxed">
            {d.total_expenses > d.total_income * 0.8 && d.total_income > 0 ? 'Your expenses are over 80% of income. Review spending categories for savings.' : d.total_income > 0 ? 'Spending is within budget. Consider investing surplus in NEPSE or mutual funds.' : 'Start tracking your income & expenses for personalized AI insights.'}
          </p>
          <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#D98324]/10 rounded-full blur-2xl" />
        </div>
      </div>

      {(d.banks || []).length > 0 && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5" data-testid="bank-overview">
          <h3 className="text-base font-semibold text-[#1C1B1A] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Accounts</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.banks.map((b) => (
              <div key={b.id} className="bg-[#FBF9F6] rounded-xl p-3 border border-[#E8E6E1]">
                <p className="text-xs text-[#5C5A56]">{b.bank_name}</p>
                <p className="text-lg font-bold font-mono-numbers text-[#1C1B1A]">{fmt(b.balance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300" data-testid={`card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5A56]">{label}</span>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}><Icon size={14} weight="duotone" style={{ color }} /></div>
      </div>
      <p className="text-lg font-bold font-mono-numbers" style={{ color }}>{value}</p>
    </div>
  );
}

function MiniCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-all duration-300">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${highlight ? 'bg-[#A63C24]/10' : 'bg-[#FBF9F6]'}`}>
        <Icon size={16} weight="duotone" className={highlight ? 'text-[#A63C24]' : 'text-[#5C5A56]'} />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#5C5A56]">{label}</p>
        <p className={`text-base font-bold font-mono-numbers ${highlight ? 'text-[#A63C24]' : 'text-[#1C1B1A]'}`}>{value}</p>
      </div>
    </div>
  );
}
